// REMVAR Fase 1 (2026-08-04) — aba Créditos do negócio.
// Registra QUEM fez O QUÊ (originação / proposta / fechamento) — o insumo da
// remuneração variável comercial. MODO OBSERVAÇÃO: nada aqui calcula nem paga;
// o motor entra na Fase 2.
//
// Regras que a UI espelha (o backend valida de novo):
// - soma dos pesos de cada papel = 100 (um papel pode ser dividido);
// - originação trava depois de prospecção/qualificação (deal registration);
// - papel vazio é estado válido: a fatia fica com a empresa.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, InputNumber, Select, Space, Spin, Tag, Tooltip, Typography, message,
} from 'antd';
import { PlusOutlined, LockOutlined, EditOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { usePerms } from '../../hooks/usePerms';

const PAPEIS: Array<{ key: string; label: string; fatia: string; dica: string }> = [
  { key: 'originacao', label: 'Originação', fatia: '30%',
    dica: 'Trouxe o lead ou abriu a porta. Registra-se cedo (prospecção/qualificação) e trava depois.' },
  { key: 'proposta', label: 'Proposta', fatia: '30%',
    dica: 'Qualificou, montou composição, escreveu e enviou.' },
  { key: 'fechamento', label: 'Fechamento', fatia: '40%',
    dica: 'Negociou e converteu.' },
];

const CANAIS = [
  { value: 'indicacao_interna', label: 'Indicação interna' },
  { value: 'carteira', label: 'Carteira' },
  { value: 'prospeccao_ativa', label: 'Prospecção ativa' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'rede_socio', label: 'Rede do sócio' },
  { value: 'licitacao_publica', label: 'Licitação pública' },
  { value: 'parceiro', label: 'Parceiro' },
];

const STATUS_VENDA_TAG: Record<string, { cor: string; rotulo: string }> = {
  novo: { cor: 'green', rotulo: 'Cliente novo (1,00×)' },
  recompra: { cor: 'blue', rotulo: 'Recompra (0,50×)' },
  aditivo: { cor: 'orange', rotulo: 'Aditivo (0,25×)' },
};

type Credito = { id: number; usuario_email: string; papel: string; peso: number; registrado_por?: string };
type Usuario = { email: string; nome: string };

export function AbaCreditos({ negocioId, reloadFlag }: { negocioId: number; reloadFlag?: number }) {
  // O canal de origem é dado do negócio: régua de editar o negócio (05/10/2026).
  const { pode, erro: permsErro } = usePerms();
  const podeEditarNegocio = permsErro || pode('comercial.negocio_privado.editar_dados');
  const [dados, setDados] = useState<any>(null);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [sugestoes, setSugestoes] = useState<any[]>([]);
  const [erro, setErro] = useState('');
  // papel em edição + linhas do editor
  const [editando, setEditando] = useState<string | null>(null);
  const [linhas, setLinhas] = useState<Array<{ usuario_email: string; peso: number }>>([]);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'com_listar_creditos', proposta_id: negocioId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setDados(d); setErro('');
      // REMVAR: indicações abertas pra este cliente — sugestão, nunca automático
      try {
        const rs = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'com_sugerir_indicacoes', proposta_id: negocioId },
          { headers: { Authorization: token } });
        const ds = rs.data.body ? JSON.parse(rs.data.body) : rs.data;
        setSugestoes(ds.sugestoes || []);
      } catch { setSugestoes([]); }
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    }
  }, [negocioId]);

  async function aceitarIndicacao(indicacaoId: number) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'com_vincular_indicacao', indicacao_id: indicacaoId, proposta_id: negocioId },
        { headers: { Authorization: token } });
      message.success('Originação creditada a quem indicou — ele foi avisado.');
      await carregar();
    } catch (e: any) { message.error(e.response?.data?.error || e.message, 6); }
  }

  useEffect(() => { carregar(); }, [carregar, reloadFlag]);
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } });
        const lista = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios((Array.isArray(lista) ? lista : [])
          .filter((u: any) => u.enabled !== false)
          .map((u: any) => ({ email: u.email, nome: u.nome || u.email })));
      } catch { /* select fica só com e-mails já registrados */ }
    })();
  }, []);

  if (erro) return <Alert type="error" showIcon message={erro} />;
  if (!dados) return <Spin />;

  const p = dados.proposta || {};
  const creditos: Credito[] = dados.creditos || [];
  const nomeDe = (email: string) =>
    usuarios.find(u => u.email.toLowerCase() === (email || '').toLowerCase())?.nome || email;

  function abrirEditor(papel: string) {
    const atuais = creditos.filter(c => c.papel === papel)
      .map(c => ({ usuario_email: c.usuario_email, peso: Number(c.peso) }));
    setLinhas(atuais.length ? atuais : []);
    setEditando(papel);
  }

  async function salvarPapel() {
    if (!editando) return;
    const soma = linhas.reduce((s, l) => s + (Number(l.peso) || 0), 0);
    if (linhas.length > 0 && Math.abs(soma - 100) > 0.01) {
      message.warning(`A soma dos pesos tem que dar 100 — está em ${soma.toFixed(0)}.`);
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'com_definir_creditos_papel', proposta_id: negocioId, papel: editando, itens: linhas },
        { headers: { Authorization: token } });
      message.success(linhas.length ? 'Créditos registrados.' : 'Papel removido — a fatia fica com a empresa.');
      setEditando(null);
      await carregar();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message, 6);
    } finally { setSalvando(false); }
  }

  async function salvarCanal(canal: string | null) {
    try {
      const token = await getToken();
      await axios.put(`${API_URL}/comercial/propostas/${negocioId}`,
        { canal_origem: canal }, { headers: { Authorization: token } });
      message.success('Canal de origem salvo.');
      await carregar();
    } catch (e: any) { message.error(e.response?.data?.error || e.message); }
  }

  const statusTag = p.cliente_status_venda ? STATUS_VENDA_TAG[p.cliente_status_venda] : null;
  const ganhoSemCredito = p.status === 'ganho' && creditos.length === 0;

  return (
    <div style={{ maxWidth: 760 }}>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Modo observação"
        description="Os créditos alimentam o plano de remuneração variável, que ainda não paga nada — esta fase só registra quem participou de cada conquista." />

      {ganhoSemCredito && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Negócio ganho sem nenhum crédito registrado"
          description="Registre quem fez a proposta e o fechamento — sem isso a conquista não conta pra ninguém." />
      )}

      {sugestoes.length > 0 && (
        <Alert type="success" showIcon style={{ marginBottom: 12 }}
          message={sugestoes.length === 1
            ? 'Alguém indicou esta empresa antes deste negócio existir'
            : `${sugestoes.length} pessoas indicaram esta empresa antes deste negócio existir`}
          description={
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {sugestoes.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Typography.Text style={{ fontSize: 12 }}>
                    <strong>{nomeDe(s.indicador_email)}</strong> em{' '}
                    {new Date(s.criado_em).toLocaleDateString('pt-BR')}
                    {s.assunto ? ` · ${s.assunto}` : ''}
                    {s.casou_por === 'nome' && (
                      <em style={{ color: '#B07500' }}> (casou pelo nome “{s.empresa_nome}”)</em>
                    )}
                  </Typography.Text>
                  <Button size="small" type="primary" onClick={() => aceitarIndicacao(s.id)}>
                    Creditar originação a {nomeDe(s.indicador_email).split(' ')[0]}
                  </Button>
                </div>
              ))}
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Aceitar substitui a originação atual, se houver. Se o negócio não veio dessas
                indicações, ignore — elas continuam abertas para outro negócio.
              </Typography.Text>
            </Space>} />
      )}

      <Space size={8} style={{ marginBottom: 16 }} wrap>
        {statusTag && <Tag color={statusTag.cor}>{statusTag.rotulo}</Tag>}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Canal de origem:</Typography.Text>
        <Select size="small" allowClear placeholder="— definir —" style={{ minWidth: 190 }}
          showSearch optionFilterProp="label" disabled={!podeEditarNegocio}
          value={p.canal_origem || undefined}
          onChange={(v) => salvarCanal(v ?? null)}
          options={CANAIS} />
      </Space>

      {PAPEIS.map(papel => {
        const doPapel = creditos.filter(c => c.papel === papel.key);
        const travado = papel.key === 'originacao' && dados.originacao_travada;
        const emEdicao = editando === papel.key;
        return (
          <div key={papel.key} style={{ border: '0.5px solid #e5e5e5', borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <span>
                <Typography.Text strong>{papel.label}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                  {papel.fatia} do pool
                </Typography.Text>
                <Tooltip title={papel.dica}>
                  <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 6, cursor: 'help' }}>ⓘ</Typography.Text>
                </Tooltip>
                {travado && (
                  <Tooltip title="Deal registration: originação só se registra em prospecção/qualificação. Alteração agora exige admin.">
                    <Tag color="default" style={{ marginLeft: 8 }}><LockOutlined /> travada</Tag>
                  </Tooltip>
                )}
              </span>
              {!emEdicao && (
                <Button size="small" icon={<EditOutlined />} onClick={() => abrirEditor(papel.key)}>
                  {doPapel.length ? 'Editar' : 'Registrar'}
                </Button>
              )}
            </Space>

            {!emEdicao && (
              <div style={{ marginTop: 8 }}>
                {doPapel.length === 0
                  ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      — sem registro (a fatia fica com a empresa)
                    </Typography.Text>
                  : doPapel.map(c => (
                      <Tag key={c.id} style={{ marginBottom: 4 }}>
                        {nomeDe(c.usuario_email)} · {Number(c.peso).toFixed(0)}%
                        {String(c.registrado_por || '').startsWith('auto:') && (
                          <Tooltip title="Crédito automático na geração da venda — edite se a divisão for outra">
                            <span style={{ opacity: 0.6 }}> (auto)</span>
                          </Tooltip>
                        )}
                        {String(c.registrado_por || '').startsWith('backfill:') && (
                          <Tooltip title="Registrado pelo backfill da Fase 1 a partir do vendedor da proposta">
                            <span style={{ opacity: 0.6 }}> (backfill)</span>
                          </Tooltip>
                        )}
                      </Tag>
                    ))}
              </div>
            )}

            {emEdicao && (
              <div style={{ marginTop: 8 }}>
                {linhas.map((l, i) => (
                  <Space key={i} style={{ marginBottom: 6 }}>
                    <Select showSearch style={{ minWidth: 260 }} size="small"
                      value={l.usuario_email || undefined}
                      placeholder="pessoa…" optionFilterProp="label"
                      onChange={(v) => setLinhas(cur => cur.map((x, j) => j === i ? { ...x, usuario_email: v } : x))}
                      options={usuarios.map(u => ({ label: `${u.nome} (${u.email})`, value: u.email }))} />
                    <InputNumber size="small" min={1} max={100} value={l.peso} addonAfter="%"
                      onChange={(v) => setLinhas(cur => cur.map((x, j) => j === i ? { ...x, peso: Number(v) || 0 } : x))} />
                    <Button size="small" danger type="text"
                      onClick={() => setLinhas(cur => cur.filter((_, j) => j !== i))}>remover</Button>
                  </Space>
                ))}
                <div>
                  <Button size="small" type="dashed" icon={<PlusOutlined />}
                    onClick={() => setLinhas(cur => [...cur,
                      { usuario_email: '', peso: cur.length === 0 ? 100 : 0 }])}>
                    Adicionar pessoa
                  </Button>
                </div>
                <Space style={{ marginTop: 10 }}>
                  <Button size="small" type="primary" loading={salvando} onClick={salvarPapel}>Salvar</Button>
                  <Button size="small" onClick={() => setEditando(null)}>Cancelar</Button>
                  {linhas.length > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      soma: {linhas.reduce((s, l) => s + (Number(l.peso) || 0), 0).toFixed(0)}% (precisa dar 100)
                    </Typography.Text>
                  )}
                </Space>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
